/**
 * Corps de la modale d’édition : formulaire (étape 1) ou sélection des références (étape 2).
 * État local via reducer, réinitialisé à chaque ouverture grâce à `key={booster.id}`.
 */
import { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { TrendingUp } from 'lucide-react';
import { Skeleton } from '@ui/skeleton.tsx';
import { DialogDescription, DialogHeader, DialogTitle } from '@ui/dialog.tsx';
import type { FC } from 'react';
import {
  getApiBoostersIdGetQueryKey,
  useApiAdminboostersIdPatch
} from '@r/api/generated/client.ts';
import type {
  BoosterBoosterReadModel,
  BoosterReferenceBoosterWriteModel,
  ReferenceReferenceReadModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { getOrvalErrorMessage } from '@r/api/orval-error-helper.ts';
import { CreateBoosterForm, type BoosterFormField } from './create-booster-form.tsx';
import { type BoosterDraftData, toBoosterPatchModel } from './create-booster-form-schema.ts';
import { SelectBoosterReferencesStep } from './select-booster-references-step.tsx';
import { boosterToDraftData, hasBoosterStarted } from './booster-utils.ts';
import { invalidateBoostersListQueries } from './use-boosters-collection.ts';
import {
  boosterReferencesReadToWriteModel,
  boosterReferencesToCustomTierMap,
  uniqueReferencesFromBoosterReferences
} from './booster-reference-tiers.ts';
import type { BoosterReferencesSelectionState } from './booster-references-selection.ts';

const LOCKED_AFTER_START_FIELDS: BoosterFormField[] = ['startDate', 'endDate', 'coefficient'];

type EditBoosterStep = 'form' | 'references';

interface EditBoosterDialogState {
  step: EditBoosterStep;
  draft: BoosterDraftData;
  originalDraft: BoosterDraftData;
  originalReferences: ReferenceReferenceReadModel[];
  originalBoosterReferences: BoosterReferenceBoosterWriteModel[];
  referencesSelection: BoosterReferencesSelectionState;
}

type EditBoosterDialogAction =
  | { type: 'CONTINUE_TO_REFERENCES'; draft: BoosterDraftData }
  | { type: 'BACK_TO_FORM' }
  | { type: 'SET_REFERENCES_SELECTION'; selection: BoosterReferencesSelectionState };

/** Initialise le reducer d’édition à partir du GET booster (draft + sélection + paliers). */
function createEditStateFromBooster(booster: BoosterBoosterReadModel): EditBoosterDialogState {
  const draft = boosterToDraftData(booster);
  const boosterReferences = booster.boosterReferences ?? [];
  const originalReferences = uniqueReferencesFromBoosterReferences(
    boosterReferences
  ) as ReferenceReferenceReadModel[];
  const customTiersByReferenceId = boosterReferencesToCustomTierMap(
    boosterReferences,
    draft.coefficient
  );

  return {
    step: 'form',
    draft,
    originalDraft: draft,
    originalReferences,
    originalBoosterReferences: boosterReferencesReadToWriteModel(boosterReferences),
    referencesSelection: {
      selectedReferenceIds: originalReferences.map((reference) => reference.id),
      customTiersByReferenceId
    }
  };
}

/** Transitions formulaire ↔ étape références et mise à jour de la sélection. */
function editBoosterDialogReducer(
  state: EditBoosterDialogState,
  action: EditBoosterDialogAction
): EditBoosterDialogState {
  switch (action.type) {
    case 'CONTINUE_TO_REFERENCES':
      return { ...state, draft: action.draft, step: 'references' };
    case 'BACK_TO_FORM':
      return { ...state, step: 'form' };
    case 'SET_REFERENCES_SELECTION':
      return { ...state, referencesSelection: action.selection };
    default:
      return state;
  }
}

interface EditBoosterDialogContentProps {
  booster: BoosterBoosterReadModel;
  boosterId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Contenu de la modale d'édition, remonté à chaque ouverture via une clé React. */
export const EditBoosterDialogContent: FC<EditBoosterDialogContentProps> = ({
  booster,
  boosterId,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(
    editBoosterDialogReducer,
    booster,
    createEditStateFromBooster
  );

  const hasStarted = useMemo(
    () => hasBoosterStarted(state.originalDraft.startDate),
    [state.originalDraft.startDate]
  );

  const referencesLabel = useMemo(() => {
    if (state.originalReferences.length === 0) {
      return t('boosters.list.references.empty');
    }
    return state.originalReferences.map((reference) => reference.uniqueCode).join(' ; ');
  }, [state.originalReferences, t]);

  const updateBoosterMutation = useApiAdminboostersIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('boosters.edit.success'));
        invalidateBoostersListQueries(queryClient);
        queryClient.invalidateQueries({
          queryKey: getApiBoostersIdGetQueryKey(String(boosterId))
        });
        onClose();
        onSuccess?.();
      },
      onError: (error) => {
        showOrvalErrorToast(error, getOrvalErrorMessage(error, t('boosters.edit.error')));
      }
    }
  });

  const handleSave = (updatedDraft: BoosterDraftData) => {
    const patchData = toBoosterPatchModel(
      state.originalDraft,
      updatedDraft,
      state.originalBoosterReferences,
      state.referencesSelection.selectedReferenceIds,
      state.referencesSelection.customTiersByReferenceId,
      hasStarted
    );

    if (Object.keys(patchData).length === 0) {
      toast.info(t('boosters.edit.no.changes'));
      onClose();
      return;
    }

    updateBoosterMutation.mutate({ id: String(boosterId), data: patchData });
  };

  const handleFormNext = (updatedDraft: BoosterDraftData) => {
    if (hasStarted) {
      handleSave(updatedDraft);
      return;
    }

    dispatch({ type: 'CONTINUE_TO_REFERENCES', draft: updatedDraft });
  };

  const dialogTitle =
    state.step === 'references'
      ? t('boosters.edit.references.title.with.name', { title: state.draft.title })
      : t('boosters.edit.dialog.title');

  const dialogDescription =
    state.step === 'references'
      ? t('boosters.edit.references.subtitle')
      : t('boosters.edit.dialog.description');

  if (state.step === 'form') {
    return (
      <div className="space-y-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 text-2xl">
            <TrendingUp className="w-6 h-6" />
            {dialogTitle}
          </DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="mt-6">
          <CreateBoosterForm
            defaultValues={state.draft}
            disabledFields={hasStarted ? LOCKED_AFTER_START_FIELDS : []}
            showLockedFieldsMessage={hasStarted}
            isSubmitting={updateBoosterMutation.isPending}
            submitLabel={
              hasStarted ? t('boosters.edit.save') : t('boosters.edit.continue.references')
            }
            onNext={handleFormNext}
          />
        </div>

        {hasStarted && (
          <div className="px-4 py-3 rounded-lg border border-border/60">
            <p className="text-sm font-medium">{t('boosters.list.references.label')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{referencesLabel}</p>
          </div>
        )}
      </div>
    );
  }

  if (hasStarted) {
    return <Skeleton className="w-full h-96 rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2.5 text-2xl">
          <TrendingUp className="w-6 h-6" />
          {dialogTitle}
        </DialogTitle>
        <DialogDescription>{dialogDescription}</DialogDescription>
      </DialogHeader>

      <SelectBoosterReferencesStep
        mode="edit"
        boosterId={boosterId}
        draft={state.draft}
        originalDraft={state.originalDraft}
        pinnedReferences={state.originalReferences}
        originalBoosterReferences={state.originalBoosterReferences}
        referencesSelection={state.referencesSelection}
        onReferencesSelectionChange={(selection) =>
          dispatch({ type: 'SET_REFERENCES_SELECTION', selection })
        }
        onBack={() => dispatch({ type: 'BACK_TO_FORM' })}
        onSuccess={() => {
          onClose();
          onSuccess?.();
        }}
      />
    </div>
  );
};
